/**
 * =============================================================================
 * collection.js
 * =============================================================================
 * Collection page functionality
 * =============================================================================
 */

var Collection = {
  container: undefined,
  sectionId: undefined, // ID of the results page section, so we know what section to request via fetch
  grid: undefined,
  endless_mode: undefined, // Data injected by collection-grid.liquid
  collectionHandle: undefined,
  bulkBuyEnabled: true,
  searchInput: undefined,
  searchClear: undefined,
  searchPanel: undefined,
  productIndex: null, // Cached products.json index for search
  productIndexPromise: null,
  facetIdCache: {}, // filter params string -> Set of product ids
  searchActive: false,
  searchRequestId: 0,
  noResultsMessage: 'No products matched your search.',
  tileBatchSize: 20,

  /**
   * Initialisation
   */
  init: function() {
    this.container = document.getElementById("products-container");
    if(!this.container){
      return;
    }
    this.grid = document.getElementById("products-grid");
    this.sectionId = this.container.dataset.sectionId;
    this.collectionHandle = this.container.dataset.collectionHandle;
    this.bulkBuyEnabled = this.container.dataset.bulkBuyEnabled !== 'false';
    this.searchPanel = this.container.querySelector('[data-collection-search]');
    this.searchInput = this.container.querySelector('[data-collection-search-input]');
    this.searchClear = this.container.querySelector('[data-collection-search-clear]');
    if (this.searchPanel?.dataset.noResults) {
      this.noResultsMessage = this.searchPanel.dataset.noResults;
    }

    this.setupEventListeners();
  },

  /**
   * Setup event listeners
   */
  setupEventListeners: function() {
    // Listen for 'load more' clicks, trigger the load. We need to listen on the
    // container, rather than directly on the button, as the button is
    // re-rendered / dynamically injected each time a filter is applied.
    this.container.addEventListener('click', function(event) {
      if(event.target.closest("a")?.classList.contains("load-more")){
        if (this.searchActive) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
        this.onLoadMoreClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
      };
    }.bind(this));

    // Setup a waypoint for when reaching the end of the products grid, when
    // pagination is endless (on scroll)
    if(this.endless_mode === "scroll"){
      const waypoint = new Waypoint({
        element: document.getElementById('products-container'),
        handler: function(direction) {
          if(direction === "down"){
            if (this.searchActive) {
              return;
            }
            const loadMoreButton = this.container.querySelector(".load-more");
            if(loadMoreButton){
              loadMoreButton.click();
            }
          }
        }.bind(this),
        offset: 'bottom-in-view'
      })
    }

    if (this.searchInput) {
      this.debouncedSearch = debounce((event) => {
        this.onSearchInput(event);
      }, 300);

      this.searchInput.addEventListener('input', function(event) {
        this.updateClearButtonVisibility();
        this.debouncedSearch(event);
      }.bind(this));
    }

    if (this.searchClear) {
      this.searchClear.addEventListener('click', function() {
        this.searchInput.value = '';
        this.updateClearButtonVisibility();
        this.clearSearch();
        this.searchInput.focus();
      }.bind(this));
    }

    // Facet filtering replaces the grid — if a text search is still active,
    // re-run it so filter clicks don't wipe the customer's search results.
    document.addEventListener('collection:filtered', function() {
      if (!this.searchInput) return;
      const query = this.searchInput.value.trim();
      if (!query) {
        this.searchActive = false;
        this.setPaginationVisible(true);
        return;
      }
      this.runSearch(query);
    }.bind(this));
  },

  /**
   * Show / hide the clear button based on input value
   */
  updateClearButtonVisibility: function() {
    if (!this.searchClear) return;
    const hasValue = this.searchInput.value.trim().length > 0;
    this.searchClear.classList.toggle('d-none', !hasValue);
  },

  /**
   * Handle search input changes
   *
   * @param {Event} event
   */
  onSearchInput: function(event) {
    const query = (event.target.value || '').trim();

    if (!query) {
      this.clearSearch();
      return;
    }

    this.runSearch(query);
  },

  /**
   * Clear search and restore the normal collection grid
   */
  clearSearch: function() {
    this.searchRequestId += 1;
    const wasSearching = this.searchActive;
    this.searchActive = false;
    this.setPaginationVisible(true);
    if (wasSearching) {
      this.restoreCollectionGrid();
    }
  },

  /**
   * Run a collection-scoped product search, intersected with active facets
   *
   * @param {String} query
   */
  runSearch: async function(query) {
    const requestId = ++this.searchRequestId;
    this.searchActive = true;
    this.setPaginationVisible(false);
    this.showLoading(true);

    try {
      const [products, facetProductIds] = await Promise.all([
        this.loadProductIndex(),
        this.loadFacetFilteredProductIds()
      ]);
      if (requestId !== this.searchRequestId) return;

      let matches = products.filter((product) => this.productMatchesQuery(product, query));

      // When facets are active, only keep products that also match those filters
      if (facetProductIds) {
        matches = matches.filter((product) => facetProductIds.has(String(product.id)));
      }

      if (requestId !== this.searchRequestId) return;

      if (matches.length === 0) {
        this.renderSearchEmpty();
        return;
      }

      const tiles = await this.fetchTilesForHandles(matches.map((product) => product.handle));
      if (requestId !== this.searchRequestId) return;

      this.renderSearchTiles(tiles);
    } catch (error) {
      console.error('Collection search failed', error);
      if (requestId === this.searchRequestId) {
        this.renderSearchEmpty();
      }
    } finally {
      if (requestId === this.searchRequestId) {
        this.showLoading(false);
      }
    }
  },

  /**
   * Current facet filter query string (filter.* only), or empty if none
   *
   * @returns {String}
   */
  getFacetFilterParams: function() {
    const params = new URLSearchParams(window.location.search);
    const filtered = new URLSearchParams();

    params.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        filtered.append(key, value);
      }
    });

    return filtered.toString();
  },

  /**
   * Load product IDs that match the active Shopify facets (all pages).
   * Returns null when no facets are active (no extra constraint).
   *
   * @returns {Promise<Set<String>|null>}
   */
  loadFacetFilteredProductIds: async function() {
    const filterParams = this.getFacetFilterParams();
    if (!filterParams) {
      return null;
    }

    if (this.facetIdCache[filterParams]) {
      return this.facetIdCache[filterParams];
    }

    const ids = new Set();
    let page = 1;
    const maxPages = 40;

    while (page <= maxPages) {
      const params = new URLSearchParams(filterParams);
      params.set('section_id', this.sectionId);
      params.set('page', String(page));

      const response = await fetch(`${window.location.pathname}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to load facet-filtered products (${response.status})`);
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const pageIds = Array.from(doc.querySelectorAll('#products-grid [data-product-id]'))
        .map((element) => String(element.dataset.productId));

      if (pageIds.length === 0) {
        break;
      }

      pageIds.forEach((id) => ids.add(id));

      if (!doc.querySelector('#pagination .load-more')) {
        break;
      }

      page += 1;
    }

    this.facetIdCache[filterParams] = ids;
    return ids;
  },

  /**
   * Load and cache the full collection product index via products.json
   *
   * @returns {Promise<Array>}
   */
  loadProductIndex: function() {
    if (this.productIndex) {
      return Promise.resolve(this.productIndex);
    }

    if (this.productIndexPromise) {
      return this.productIndexPromise;
    }

    this.productIndexPromise = this.fetchAllCollectionProducts()
      .then((products) => {
        this.productIndex = products;
        return products;
      })
      .catch((error) => {
        this.productIndexPromise = null;
        throw error;
      });

    return this.productIndexPromise;
  },

  /**
   * Paginate through /collections/{handle}/products.json
   *
   * @returns {Promise<Array>}
   */
  fetchAllCollectionProducts: async function() {
    const products = [];
    let page = 1;
    const limit = 250;

    while (true) {
      const url = `/collections/${this.collectionHandle}/products.json?limit=${limit}&page=${page}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load collection products (${response.status})`);
      }
      const data = await response.json();
      const pageProducts = data.products || [];
      products.push(...pageProducts);

      if (pageProducts.length < limit) {
        break;
      }
      page += 1;
    }

    return products;
  },

  /**
   * Whether a products.json product matches the search query
   *
   * @param {Object} product
   * @param {String} query
   * @returns {Boolean}
   */
  productMatchesQuery: function(product, query) {
    const q = this.normalizeSearchText(query);
    if (this.normalizeSearchText(product.title || '').includes(q)) return true;
    if (this.normalizeSearchText(product.vendor || '').includes(q)) return true;
    return (product.variants || []).some((variant) => this.normalizeSearchText(variant.sku || '').includes(q));
  },

  normalizeSearchText: function(text) {
    return (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  },

  /**
   * Fetch tile HTML for the given product handles (batched)
   *
   * @param {Array<String>} handles
   * @returns {Promise<Array<String>>}
   */
  fetchTilesForHandles: async function(handles) {
    const view = this.bulkBuyEnabled ? 'collection-quick-buy' : 'collection-quick-buy-single';
    const tiles = [];

    for (let i = 0; i < handles.length; i += this.tileBatchSize) {
      const batch = handles.slice(i, i + this.tileBatchSize);
      const prefixedHandles = batch.map((handle) => `handle:${handle}`).join('+OR+');
      const url = `/search?q=${prefixedHandles}&type=product&view=${view}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load search tiles (${response.status})`);
      }
      const data = await response.json();
      tiles.push(...(data.tiles || []));
    }

    return tiles;
  },

  /**
   * Replace the grid with search result tiles
   *
   * @param {Array<String>} tiles
   */
  renderSearchTiles: function(tiles) {
    this.grid.innerHTML = `<div class="row gx-2 gy-3 gy-md-2">${tiles.join('')}</div>`;
    this.afterGridUpdate();
  },

  /**
   * Show empty search state
   */
  renderSearchEmpty: function() {
    this.grid.innerHTML = `
      <div class="row gx-2 gy-3 gy-md-2">
        <div class="col-12 text-center">
          <div class="my-7 text-center">
            <h4>${this.noResultsMessage}</h4>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Restore the collection grid from the current URL (facets / pagination)
   */
  restoreCollectionGrid: function() {
    this.showLoading(true);
    const searchParams = window.location.search.slice(1);
    const url = `${window.location.pathname}?section_id=${this.sectionId}${searchParams ? `&${searchParams}` : ''}`;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        const newGrid = parsed.getElementById('products-grid');
        const newPagination = parsed.getElementById('pagination');

        if (newGrid) {
          this.grid.innerHTML = newGrid.innerHTML;
        }
        if (newPagination) {
          document.getElementById('pagination').outerHTML = newPagination.outerHTML;
        }

        this.setPaginationVisible(true);
        this.afterGridUpdate();
      })
      .finally(() => {
        this.showLoading(false);
      });
  },

  /**
   * Shared post-update hooks after grid HTML changes
   */
  afterGridUpdate: function() {
    mp_lazyload_setup();
    if (typeof Waypoint !== 'undefined') {
      Waypoint.refreshAll();
    }
    document.dispatchEvent(new CustomEvent('collection:updated'));
    document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
  },

  /**
   * Toggle pagination visibility while searching
   *
   * @param {Boolean} visible
   */
  setPaginationVisible: function(visible) {
    const pagination = document.getElementById('pagination');
    if (!pagination) return;
    pagination.classList.toggle('d-none', !visible);
  },

  /**
   * Toggle the collection loading overlay
   *
   * @param {Boolean} show
   */
  showLoading: function(show) {
    const loader = document.getElementById('results-loading-collection');
    if (!loader) return;
    loader.classList.toggle('d-none', !show);
  },

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage: function(searchParams, event, updateURLHash = true) {
    document.getElementById('results-loading-collection').classList.remove('d-none');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    this.renderSectionFromFetch(url, event);

    const params = new URLSearchParams(searchParams);
    params.delete('page');

    if (updateURLHash) {
      this.updateURLHash(params.toString());
    }
  },

  /**
   * Fetch the section and re-render
   * 
   * @param {String} url URL to fetch and render 
   */
  renderSectionFromFetch: function(url) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        this.renderNewTiles(html);
        this.renderPagination(html);
        document.getElementById('results-loading-collection').classList.add('d-none');
        mp_lazyload_setup();
        Waypoint.refreshAll();

        var event = new CustomEvent("collection:updated");
        document.dispatchEvent(event);

        // Alert SWYM so it can instantiate wishlist buttons
        document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
      });
  },

  /**
   * Extract and append the new product tiles' HTML
   * 
   * @param {String} html The HTML returned from fetch 
   */
  renderNewTiles: function(html) {
    // Extract the new product tiles from the returned HTML
    const newTiles = new DOMParser().parseFromString(html, 'text/html').querySelectorAll("[data-page]");
    const row = this.grid.querySelector('.row') || this.grid;

    // Append the new page of tiles into the guttered row
    Array.from(newTiles).forEach(function(element) {
      const fragment = document.createDocumentFragment();
      fragment.appendChild(element);
      row.appendChild(fragment);
    }.bind(this));
  },

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html The HTML returned from fetch
   */
  renderPagination: function(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  },

  /**
   * Start the process of loading in new product tiles
   * 
   * @param {Event} event The initiating event
   */
  onLoadMoreClick: function(event) {
    event.preventDefault();
    if (this.searchActive) return;
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  },

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash: function(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }
}

export default Collection;
